import { useAuth, useSpectateMatch } from '@shootout-comps/core';
import { useWatchMatchSocket, useConnectionStatus } from '../adapters/webSocket';
import { useLang } from '@shootout-comps/i18n';
import { SnookerBallSpinner } from '../components/SnookerBall';
import { SpectatorView } from '../components/SpectatorMatchView';
import ConnectionStatusBanner from '../components/ConnectionStatusBanner';

interface SpectateMatchPageProps {
  tournamentId: string;
  matchId: string;
  onBack: () => void;
}

export default function SpectateMatchPage({ tournamentId, matchId, onBack }: SpectateMatchPageProps) {
  const { getAccessToken } = useAuth();
  const { t } = useLang();
  const sm = useSpectateMatch({ matchId, tournamentId, apiBaseUrl: '', getAccessToken, useWatchMatchSocket, useConnectionStatus });

  if (sm.loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 64px)', color: 'var(--chalk-dim)' }}>
        <SnookerBallSpinner size={36} />
      </div>
    );
  }

  if (sm.error || !sm.match || !sm.p1) {
    return (
      <div style={{ maxWidth: '640px', margin: '2rem auto', padding: '0 1rem' }}>
        <button onClick={onBack} style={{ background: 'transparent', color: 'var(--chalk-dim)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px', padding: '0.4rem 0.9rem', fontSize: '0.78rem', cursor: 'pointer', minHeight: '36px' }}>
          {t.backToDraw}
        </button>
        <div style={{ marginTop: '1rem', background: 'rgba(192,57,43,0.15)', border: '1px solid var(--red)', borderRadius: '8px', padding: '1rem', color: '#e74c3c' }}>
          {sm.error ?? t.tournamentNotFound}
        </div>
      </div>
    );
  }

  const isSnooker900 = (sm.ls?.halfTimeSeconds ?? 0) === 0 && !sm.isStandard;

  return (
    <div style={{ padding: 'var(--page-pad)' }}>
      <ConnectionStatusBanner />
      <div style={{ maxWidth: '600px', margin: '0 auto 1rem' }}>
        <button onClick={onBack} style={{ background: 'transparent', color: 'var(--chalk-dim)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px', padding: '0.4rem 0.9rem', fontSize: '0.78rem', cursor: 'pointer', minHeight: '36px' }}>
          {t.backToDraw}
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.75rem', color: 'var(--chalk-dim)', fontSize: '0.78rem' }}>
          <span>{t.spectatingBadge}</span>
        </div>
      </div>
      <SpectatorView
        match={sm.match}
        ls={sm.ls}
        p1={sm.p1}
        p2={sm.p2 ?? sm.p1}
        curPlayer={sm.curPlayer}
        inProgress={sm.inProgress}
        breakOffPending={sm.breakOffPending}
        complete={sm.complete}
        remaining={sm.remaining}
        clockPaused={sm.clockPaused}
        shotLeft={sm.shotLeft}
        shotWarn={sm.shotWarn}
        shotOver={sm.shotOver}
        displayElapsed={sm.displayElapsed}
        matchDuration={sm.matchDuration}
        halfTime={sm.ls?.halfTimeSeconds ?? 0}
        inSecondHalf={sm.inSecondHalf}
        shotLimit={sm.shotLimit}
        p1Break={sm.p1Break}
        p2Break={sm.p2Break}
        p1HighBreak={sm.p1HighBreak}
        p2HighBreak={sm.p2HighBreak}
        p1AvgShotTime={sm.p1AvgShotTime}
        p2AvgShotTime={sm.p2AvgShotTime}
        p1MatchHighBreak={sm.p1MatchHighBreak}
        p2MatchHighBreak={sm.p2MatchHighBreak}
        bestOf={sm.bestOf}
        framesToWin={sm.framesToWin}
        frameWins1={sm.frameWins1}
        frameWins2={sm.frameWins2}
        currentFrame={sm.currentFrame}
        frames={sm.frames}
        fmt={isSnooker900 ? 'snooker900' : 'shootout'}
        widescreen={false}
        isStandard={sm.isStandard}
        t={t}
      />
    </div>
  );
}
